import { err, ok, Result } from "neverthrow";
import { match } from "ts-pattern";
import z from "zod";

import {
  buildInvalidError,
  getErrorMessage,
  type InvalidError,
} from "@thalamos/common";

import {
  CATEGORY_KEYWORD,
  COMMENT_MAX_LENGTH,
  GROUP_CATEGORIES,
  MEANING_KEYWORD,
  SUPPLEMENT_KEYWORD,
  SUPPLEMENT_KINDS,
  type Supplement,
} from "./authoring.js";
import { canonicalise } from "./canonical.js";

/**
 * The published form of an instrument: JSON Schema, draft 2020-12, and its
 * `$id`. This is what is digested, stored, served to a caller validating
 * against it, and read by anything that needs to know what an instrument asks.
 *
 * The schema below describes the subset an instrument may be published in — a
 * strict object of strict groups of items, each item one response, where an
 * item may be declared a supplement to another in its group, such as a comment
 * on it — and parsing against it is what lets everything downstream read a
 * published instrument without introspecting zod. An instrument whose schema
 * does not fit this cannot be published, which is the point: the subset is
 * narrow enough that a 2020-12 validator, a form generator and a reader of the
 * rows all agree about what
 * they are looking at.
 */

/** A response's own schema, once any reference into `$defs` is resolved. */
const ResponseSchema = z.looseObject({});

/**
 * A `required` list that names only properties the object beside it defines.
 *
 * A `required` naming a property the object never declares describes a document
 * nothing can satisfy, and no reader built from `properties` can see it at all.
 * Loose about what it does *not* name: a property absent from `required` is
 * optional, which is what optional means.
 */
const requiredNamesProperties = (value: {
  properties: Readonly<Record<string, unknown>>;
  required?: readonly string[];
}): boolean =>
  // `Object.hasOwn` rather than `in`, which answers for every inherited member:
  // a schema requiring "toString" would otherwise pass while declaring nothing.
  (value.required ?? []).every((key) => Object.hasOwn(value.properties, key));

/**
 * A comment's response: prose about another item's answer, bounded by
 * `COMMENT_MAX_LENGTH`.
 *
 * The bound is literal here because a stored schema is what a submission is
 * validated against — a row declaring a numeric or unbounded comment would
 * leave the bound to whatever collected the document.
 */
const CommentSchema = z.strictObject({
  type: z.literal("string"),
  maxLength: z.literal(COMMENT_MAX_LENGTH),
});

/**
 * Text in at least one language: a map naming none describes nothing, and a
 * text shown is always in a named language, so no key may be empty.
 */
const LanguageMapSchema = z
  .record(z.string().min(1), z.string())
  .refine((languages) => Object.keys(languages).length > 0, {
    error: "names no language",
  });

const meaningFields = {
  title: LanguageMapSchema,
  description: LanguageMapSchema.optional(),
};

const GroupMeaningSchema = z.strictObject(meaningFields);

/** Which of an item's values an answer option is: we model a closed set's values as strings or numbers only. */
const AnswerOptionValueSchema = z.union([z.string(), z.number()]);

const ItemMeaningSchema = z.strictObject({
  ...meaningFields,
  answers: z
    .array(z.strictObject({ value: AnswerOptionValueSchema, ...meaningFields }))
    .optional(),
});

const ItemSchema = z.strictObject({
  type: z.literal("object"),
  properties: z.strictObject({ response: z.looseObject({}) }),
  required: z.tuple([z.literal("response")]),
  additionalProperties: z.literal(false),
  [MEANING_KEYWORD]: ItemMeaningSchema.optional(),
  [SUPPLEMENT_KEYWORD]: z
    .strictObject({ to: z.string(), as: z.enum(SUPPLEMENT_KINDS) })
    .optional(),
});

const GroupSchema = z
  .strictObject({
    type: z.literal("object"),
    properties: z.record(z.string(), ItemSchema),
    required: z.array(z.string()).optional(),
    additionalProperties: z.literal(false),
    [MEANING_KEYWORD]: GroupMeaningSchema.optional(),
    [CATEGORY_KEYWORD]: z.enum(GROUP_CATEGORIES).optional(),
  })
  .refine(requiredNamesProperties, {
    error: "a group requires an item it does not declare",
  });

/**
 * The one dialect an instrument is published in. A literal rather than a
 * string: a document claiming another dialect is not one of ours, and pinning
 * it here is also what lets a validator take a parsed schema without a cast.
 */
export const JSON_SCHEMA_DIALECT =
  "https://json-schema.org/draft/2020-12/schema";

export const PublishedInstrumentSchemaSchema = z
  .strictObject({
    $schema: z.literal(JSON_SCHEMA_DIALECT),
    $id: z.string(),
    type: z.literal("object"),
    properties: z.record(z.string(), GroupSchema),
    required: z.array(z.string()).optional(),
    additionalProperties: z.literal(false),
    $defs: z.record(z.string(), ResponseSchema).optional(),
  })
  .refine(requiredNamesProperties, {
    error: "an instrument requires a group it does not declare",
  });

export type PublishedInstrumentSchema = z.infer<
  typeof PublishedInstrumentSchemaSchema
>;

export type PublishedGroupSchema = z.infer<typeof GroupSchema>;

export type PublishedItemSchema = z.infer<typeof ItemSchema>;

/** The item a comment is about, or `undefined` for an item that is not a comment. */
export const commentTargetOf = (
  itemSchema: PublishedItemSchema,
): string | undefined => {
  const supplement: Supplement | undefined = itemSchema[SUPPLEMENT_KEYWORD];

  return supplement === undefined
    ? undefined
    : match(supplement.as)
        .with("comment", () => supplement.to)
        .exhaustive();
};

/** The key of each comment a group declares, by the key of the item it is about. */
export const commentKeysIn = (
  groupSchema: PublishedGroupSchema,
): ReadonlyMap<string, string> =>
  new Map(
    Object.entries(groupSchema.properties).flatMap(([key, itemSchema]) => {
      const target = commentTargetOf(itemSchema);

      return target === undefined ? [] : [[target, key] as const];
    }),
  );

/** One item of a published instrument, and where it sits. */
export type PublishedItem = {
  group: string;
  item: string;
  /** Whether a valid document must carry it, given its group is present. */
  required: boolean;
  /** The item it is a comment on, or `undefined` for an item that is not a comment. */
  commentOn: string | undefined;
  /**
   * What its response accepts, as one comparable string, with any reference
   * into `$defs` already resolved — so two items drawing on the same response
   * set compare equal whether or not they were written to share a schema. That
   * is what "a key means one thing" is decided on.
   */
  shape: string;
};

/**
 * Whether a schema is one a validator can actually be built from.
 *
 * The structure above says a response *is* a schema without saying which, so a
 * response referring to a definition that is not there passes it. Building the
 * validator is what settles that, and doing it here means a corrupt row is
 * refused when it is read rather than when the next submission arrives.
 */
const buildable = Result.fromThrowable(
  (schema: PublishedInstrumentSchema) => z.fromJSONSchema(schema),
  (cause) => `no validator can be built from it: ${getErrorMessage(cause)}`,
);

/**
 * A response's own schema, following a reference into the document's
 * definitions, or `undefined` where the reference names nothing defined.
 */
export const resolveResponse = (
  response: Readonly<Record<string, unknown>>,
  defs: Readonly<Record<string, Readonly<Record<string, unknown>>>>,
): Readonly<Record<string, unknown>> | undefined => {
  const reference = response["$ref"];

  return typeof reference === "string"
    ? defs[reference.replace(/^#\/\$defs\//, "")]
    : response;
};

const DEFINITION_PREFIX = "#/$defs/";

/**
 * The id of the shared response set a response draws on, which publishing
 * emits as the name of the definition it refers to; `undefined` for a
 * response written inline or referring anywhere else.
 */
export const responseSetOf = (
  response: Readonly<Record<string, unknown>>,
): string | undefined => {
  const reference = response["$ref"];

  return typeof reference === "string" &&
    reference.startsWith(DEFINITION_PREFIX)
    ? reference.slice(DEFINITION_PREFIX.length)
    : undefined;
};

/**
 * The values a response allows, where it is a closed set (an `enum`, or a
 * `const` as a set of one), following any reference into the document's
 * definitions; `undefined` for a response of a whole JSON type, or one
 * referring to a definition the document lacks.
 */
export const closedValuesOf = (
  response: Readonly<Record<string, unknown>>,
  defs: Readonly<Record<string, Readonly<Record<string, unknown>>>>,
): readonly unknown[] | undefined => {
  const resolved = resolveResponse(response, defs);
  const values: unknown =
    resolved !== undefined && "const" in resolved
      ? [resolved["const"]]
      : resolved?.["enum"];

  return Array.isArray(values) ? values : undefined;
};

/**
 * Where an item's meaning disagrees with the values its response allows: a
 * closed set must have every value described exactly once and nothing else,
 * and a response of a whole JSON type has no values to describe.
 *
 * Checked against the resolved response, so an item drawing on a shared set is
 * held to that set's values.
 */
const meaningIssuesIn = (schema: PublishedInstrumentSchema): string[] =>
  Object.entries(schema.properties).flatMap(([group, groupSchema]) =>
    Object.entries(groupSchema.properties).flatMap(([item, itemSchema]) => {
      const meaning = itemSchema[MEANING_KEYWORD];

      if (meaning === undefined) {
        return [];
      }

      const at = `/properties/${group}/properties/${item}`;
      const values = closedValuesOf(
        itemSchema.properties.response,
        schema.$defs ?? {},
      );

      if (values === undefined) {
        return meaning.answers === undefined
          ? []
          : [`${at}: describes answers, but its response is not a closed set`];
      }

      const described = (meaning.answers ?? []).map(({ value }) => value);

      return [
        ...values
          .filter(
            (value) =>
              described.filter((given) => given === value).length !== 1,
          )
          .map(
            (value) =>
              `${at}: must describe ${JSON.stringify(value)} exactly once`,
          ),
        ...described
          .filter((value) => !values.includes(value))
          .map(
            (value) =>
              `${at}: describes ${JSON.stringify(value)}, which its response does not allow`,
          ),
      ];
    }),
  );

/**
 * Every way a declared supplement breaks its rules: an assessor may always
 * leave it out, it adds to an item in its own group that is not itself a
 * supplement, and its response is what its kind takes (a comment's is the
 * bounded comment). Readers pair a supplement with its item through this
 * declaration, so a stored schema breaking it is refused when read.
 */
const supplementIssuesIn = (schema: PublishedInstrumentSchema): string[] =>
  Object.entries(schema.properties).flatMap(([group, groupSchema]) =>
    Object.entries(groupSchema.properties).flatMap(([item, itemSchema]) => {
      const supplement = itemSchema[SUPPLEMENT_KEYWORD];

      if (supplement === undefined) {
        return [];
      }

      const at = `/properties/${group}/properties/${item}`;
      const { to } = supplement;
      const targetSchema = Object.hasOwn(groupSchema.properties, to)
        ? groupSchema.properties[to]
        : undefined;
      const ofItsKind = match(supplement.as)
        .with("comment", () =>
          CommentSchema.safeParse(itemSchema.properties.response).success
            ? []
            : [
                `${at}: a comment's response must be a string of at most ${String(COMMENT_MAX_LENGTH)} characters`,
              ],
        )
        .exhaustive();

      return [
        ...ofItsKind,
        ...((groupSchema.required ?? []).includes(item)
          ? [
              `${at}: a supplement may always be left out, so its group cannot require it`,
            ]
          : []),
        ...(targetSchema === undefined
          ? [`${at}: is a supplement to "${to}", which its group does not ask`]
          : targetSchema[SUPPLEMENT_KEYWORD] === undefined
            ? []
            : [
                `${at}: is a supplement to "${to}", which is itself a supplement`,
              ]),
      ];
    }),
  );

/**
 * Every group left without a category where another has one. An instrument
 * categorises all its groups or none, so a reader laying answers out by
 * category has a place for every group.
 */
const categoryIssuesIn = (schema: PublishedInstrumentSchema): string[] => {
  const groups = Object.entries(schema.properties);
  const categorises = groups.some(
    ([, groupSchema]) => groupSchema[CATEGORY_KEYWORD] !== undefined,
  );

  return categorises
    ? groups
        .filter(
          ([, groupSchema]) => groupSchema[CATEGORY_KEYWORD] === undefined,
        )
        .map(
          ([group]) =>
            `/properties/${group}: declares no category, though other groups do`,
        )
    : [];
};

/**
 * Reads a published schema back, refusing anything outside the subset above,
 * any meaning that disagrees with the values it describes, any supplement
 * declared against its rules, categories on only some groups, and anything
 * `buildable` cannot make a validator from.
 *
 * The path that produces one of these already conforms; this is for the ones
 * that arrive from a database row or an API, where nothing has been checked.
 */
export const parsePublishedInstrumentSchema = (
  value: unknown,
): Result<PublishedInstrumentSchema, InvalidError> => {
  const parsed = PublishedInstrumentSchemaSchema.safeParse(value);

  if (parsed.success) {
    const schema = parsed.data;
    const refusal = (issues: string[]) =>
      buildInvalidError({
        message: "Not a published instrument schema",
        issues,
      });

    // A validator first, so a reference to a missing definition is reported
    // as that rather than as an item that is not a closed set.
    return buildable(schema)
      .mapErr((issue) => refusal([issue]))
      .andThen(() => {
        const issues = [
          ...meaningIssuesIn(schema),
          ...supplementIssuesIn(schema),
          ...categoryIssuesIn(schema),
        ];

        return issues.length === 0 ? ok(schema) : err(refusal(issues));
      });
  }

  return err(
    buildInvalidError({
      message: "Not a published instrument schema",
      issues: parsed.error.issues.map(
        (issue) => `/${issue.path.join("/")}: ${issue.message}`,
      ),
    }),
  );
};

/** The groups an instrument has, and whether a valid document must carry each. */
export const groupsOf = (
  schema: PublishedInstrumentSchema,
): { group: string; required: boolean }[] =>
  Object.keys(schema.properties).map((group) => ({
    group,
    required: (schema.required ?? []).includes(group),
  }));

/**
 * A schema fragment as one comparable string, or the fallback where it has no
 * canonical spelling. Everything reaching here parsed as a published schema, so
 * the fallback is what keeps the comparison total rather than a case with
 * meaning of its own.
 */
const spell = (value: unknown, unspellable: string): string =>
  canonicalise(value).unwrapOr(unspellable);

/**
 * What a response accepts, with any reference resolved against the document's
 * own definitions.
 *
 * A reference nothing defines answers with the reference itself: a stable
 * comparison, where saying nothing would read as "this item accepts anything".
 * Only a hand-built document can carry one, because publishing emits a
 * reference solely for a definition it also writes.
 */
const shapeOf = (
  response: Readonly<Record<string, unknown>>,
  defs: Readonly<Record<string, Readonly<Record<string, unknown>>>>,
): string => {
  const reference = response["$ref"];
  const label = typeof reference === "string" ? reference : "the response";
  const resolved = resolveResponse(response, defs);

  return resolved === undefined ? label : spell(resolved, label);
};

/** Every item of an instrument, in no particular order: order is not part of its identity. */
export const itemsOf = (schema: PublishedInstrumentSchema): PublishedItem[] =>
  Object.entries(schema.properties).flatMap(([group, groupSchema]) =>
    Object.entries(groupSchema.properties).map(([item, itemSchema]) => ({
      group,
      item,
      required: (groupSchema.required ?? []).includes(item),
      commentOn: commentTargetOf(itemSchema),
      shape: shapeOf(itemSchema.properties.response, schema.$defs ?? {}),
    })),
  );

/** The group an item belongs to, or `undefined` where the instrument does not ask it. */
export const groupOfItem = (
  schema: PublishedInstrumentSchema,
  item: string,
): string | undefined =>
  itemsOf(schema).find((candidate) => candidate.item === item)?.group;

/** What one named item's response accepts, or nothing where it is not asked. */
export const responseShapeOf = (
  schema: PublishedInstrumentSchema,
  group: string,
  item: string,
): string | undefined =>
  itemsOf(schema).find(
    (candidate) => candidate.group === group && candidate.item === item,
  )?.shape;
